import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Spinner } from "@/components/common/Common";
import { FilterChip } from "@/components/common/FilterChip";
import { useAuth } from "@/context/AuthContext";
import { toOpts, useLookups } from "@/hooks/useLookups";
import { api, fmtDate, formatError } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/constants";

const COLUMNS = ["new_join", "onboarding", "ready_for_payroll", "active"];
const colOf = (s) => (s === "active_not_paid" ? "active" : s);
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

function Card({ card, canWrite, onMove }) {
  const p = card.progress;
  return (
    <div draggable={canWrite} onDragStart={(e) => e.dataTransfer.setData("text/plain", card.id)}
      className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing" data-testid={`board-card-${card.id}`}>
      <div className="flex items-start justify-between gap-2">
        <Link to={`/contracts/${card.id}`} className="font-semibold text-sm text-slate-900 hover:text-blue-700">{card.contractor_name}</Link>
        <span className="h-6 w-6 shrink-0 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center" title={card.assigned_to_name || "Unassigned"}>{card.assigned_to_name ? initials(card.assigned_to_name) : "–"}</span>
      </div>
      <p className="text-xs text-slate-500 mt-0.5">{card.recruitment_company_name || "—"}</p>
      <p className="text-[11px] text-slate-400 mt-0.5"><span className="font-mono">{card.employment_id}</span> · starts {fmtDate(card.start_date)}</p>
      <div className="flex items-center gap-2 mt-2">
        <Progress value={p.total ? (p.done / p.total) * 100 : 0} className="h-1.5 flex-1" />
        <span className="text-[11px] font-mono text-slate-500" data-testid={`board-card-progress-${card.id}`}>{p.done}/{p.total}</span>
      </div>
      <div className="flex items-center gap-2 mt-2">
        {card.status === "active_not_paid" && <span className="text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5" data-testid={`board-card-notpaid-${card.id}`}>Active not paid</span>}
        {p.overdue > 0 && <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid={`board-card-overdue-${card.id}`}><AlertTriangle className="h-3 w-3" />{p.overdue} overdue</span>}
        {canWrite && (
          <Select onValueChange={(v) => onMove(card, v)}>
            <SelectTrigger className="h-6 ml-auto w-auto text-[11px] px-2 md:hidden" data-testid={`board-card-move-${card.id}`}><SelectValue placeholder="Move…" /></SelectTrigger>
            <SelectContent>{COLUMNS.filter((c) => c !== colOf(card.status)).map((c) => <SelectItem key={c} value={c}>{STATUS_LABELS[c]}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

export default function PipelineBoard() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const lk = useLookups();
  const [cards, setCards] = useState(null);
  const [f, setF] = useState({ assigned_to: [], recruitment_company_id: [] });
  const [pending, setPending] = useState(null);
  const [over, setOver] = useState(null);
  const [mobileCol, setMobileCol] = useState("new_join");
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v.length).map(([k, v]) => [k, v.join(",")]));
  const key = JSON.stringify(params);
  const load = useCallback(() => api.get("/board", { params: JSON.parse(key) }).then((r) => setCards(r.data.cards)), [key]);
  useEffect(() => { load(); }, [load]);

  const onMove = (card, status) => { if (colOf(card.status) !== status) setPending({ card, status }); };
  const confirm = async () => {
    try {
      await api.post(`/contracts/${pending.card.id}/status`, { status: pending.status });
      toast.success(`${pending.card.contractor_name} moved to ${STATUS_LABELS[pending.status]}`);
      setPending(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const drop = (e, col) => {
    e.preventDefault();
    setOver(null);
    const card = cards.find((c) => c.id === e.dataTransfer.getData("text/plain"));
    if (card) onMove(card, col);
  };

  return (
    <div>
      <PageHeader title="Pipeline" subtitle="Drag a card to change its status. Every move is confirmed and audited." testId="board-header"
        actions={<Button variant="outline" onClick={() => navigate("/contracts")} data-testid="board-list-view-button"><LayoutList className="h-4 w-4 mr-1" />List view</Button>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip label="Assigned to" options={[{ value: "unassigned", label: "Unassigned" }, ...toOpts(lk?.staff)]} value={f.assigned_to} onChange={(v) => setF({ ...f, assigned_to: v })} testId="board-filter-assigned" />
        <FilterChip label="Recruitment company" options={toOpts(lk?.recruitment_companies)} value={f.recruitment_company_id} onChange={(v) => setF({ ...f, recruitment_company_id: v })} testId="board-filter-company" />
      </div>
      <Tabs value={mobileCol} onValueChange={setMobileCol} className="md:hidden mb-3">
        <TabsList className="w-full bg-white border border-slate-200 h-auto flex-wrap">
          {COLUMNS.map((c) => <TabsTrigger key={c} value={c} className="text-xs" data-testid={`board-mobile-tab-${c}`}>{STATUS_LABELS[c]}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {!cards ? <div className="flex justify-center p-10"><Spinner /></div> : (
        <div className="grid md:grid-cols-4 gap-4 items-start">
          {COLUMNS.map((col) => {
            const list = cards.filter((c) => colOf(c.status) === col);
            return (
              <section key={col} onDragOver={(e) => { e.preventDefault(); setOver(col); }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, col)}
                className={`${mobileCol === col ? "block" : "hidden"} md:block rounded-lg border p-3 min-h-[300px] transition-colors ${over === col ? "bg-blue-50 border-blue-300" : "bg-slate-100/70 border-slate-200"}`}
                data-testid={`board-column-${col}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">{STATUS_LABELS[col]}</h3>
                  <span className="text-xs font-mono bg-white border border-slate-200 rounded-full px-2" data-testid={`board-count-${col}`}>{list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.map((card) => <Card key={card.id} card={card} canWrite={canWrite} onMove={onMove} />)}
                  {!list.length && <p className="text-xs text-slate-400 text-center py-6">No contracts</p>}
                </div>
              </section>
            );
          })}
        </div>
      )}
      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="board-move-dialog">
          <DialogHeader>
            <DialogTitle>Change status?</DialogTitle>
            <DialogDescription>Move {pending?.card.contractor_name} ({pending?.card.employment_id}) from {STATUS_LABELS[pending?.card.status]} to {STATUS_LABELS[pending?.status]}.</DialogDescription>
          </DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setPending(null)} data-testid="board-move-cancel">Cancel</Button><Button onClick={confirm} data-testid="board-move-confirm">Move</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
